import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ShieldCheck, FileText, Server, KeyRound, ScanEye, ScrollText, Fingerprint,
  Activity, RefreshCw, Play, Ban, CheckCircle2, Lock,
  Users, ClipboardCheck, Plus, ShieldAlert, Timer, XCircle, LifeBuoy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Honest state → visual mapping (from design_guidelines.json).
const STATE_STYLES = {
  active: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  pending_approval: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  expired: "text-slate-400 bg-slate-800 border-slate-700",
  revoked: "text-rose-500 bg-rose-500/10 border-rose-500/20",
  unknown: "text-indigo-400 bg-indigo-400/10 border-indigo-400/20",
  disabled: "text-slate-500 bg-slate-800/60 border-slate-700",
  draft: "text-slate-300 bg-slate-800 border-slate-700",
  critical: "text-rose-400 bg-rose-500/10 border-rose-500/30",
};

const StateBadge = ({ state }) => {
  const key = (state || "unknown").toLowerCase();
  const cls = STATE_STYLES[key] || STATE_STYLES.unknown;
  return (
    <span data-testid={`state-${key}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[11px] font-mono uppercase tracking-wider ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cls.split(" ")[0].replace("text-", "bg-")}`} />
      {key.replace(/_/g, " ")}
    </span>
  );
};

const Label2 = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-1">{children}</div>
);

const auth = () => ({}); // cookie auth via axios.defaults.withCredentials

export const EnterpriseInferenceConsole = () => {
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [agreement, setAgreement] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [fedAvail, setFedAvail] = useState(false);
  const [config, setConfig] = useState(null);
  const [supportGrants, setSupportGrants] = useState([]);
  const [activeSupport, setActiveSupport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  // Step-up dialog state
  const [stepUp, setStepUp] = useState({ open: false, action: null, code: "", resolve: null });

  const refresh = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const [m, s, p, d, a, c, cfg, sup, act] = await Promise.allSettled([
        axios.get(`${API}/enterprise/permissions/me`),
        axios.get(`${API}/enterprise/status`),
        axios.get(`${API}/enterprise/policies`),
        axios.get(`${API}/enterprise/deployments`),
        axios.get(`${API}/enterprise/agreement`),
        axios.get(`${API}/enterprise/identity/connectors`),
        axios.get(`${API}/enterprise/config-options`),
        axios.get(`${API}/enterprise/support-access`),
        axios.get(`${API}/enterprise/support-access/active`),
      ]);
      if (m.status === "fulfilled") setMe(m.value.data);
      if (s.status === "fulfilled") setStatus(s.value.data);
      if (p.status === "fulfilled") setPolicies(p.value.data.policies || []);
      if (d.status === "fulfilled") setDeployments(d.value.data.deployments || []);
      if (a.status === "fulfilled") setAgreement(a.value.data.agreement);
      if (c.status === "fulfilled") {
        setConnectors(c.value.data.connectors || []);
        setFedAvail(!!c.value.data.federated_identity_available);
      }
      if (cfg.status === "fulfilled") setConfig(cfg.value.data);
      if (sup.status === "fulfilled") setSupportGrants(sup.value.data.grants || []);
      if (act.status === "fulfilled") setActiveSupport(act.value.data.active || []);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(true); }, [refresh]);

  const [tab, setTab] = useState("overview");

  // Returns a fresh step-up grant for `action` (or null on cancel/fail).
  const requestStepUp = (action) =>
    new Promise((resolve) => setStepUp({ open: true, action, code: "", resolve }));

  const submitStepUp = async () => {
    const { action, code, resolve } = stepUp;
    try {
      const r = await axios.post(`${API}/enterprise/step-up/verify`, { action, code });
      setStepUp({ open: false, action: null, code: "", resolve: null });
      resolve && resolve(r.data.grant);
    } catch {
      toast.error("Step-up verification failed");
      resolve && resolve(null);
      setStepUp({ open: false, action: null, code: "", resolve: null });
    }
  };

  const can = (perm) => me?.permissions?.includes(perm);

  // ── Actions ──
  const approvePolicy = async (pol) => {
    const grant = await requestStepUp("policy_approval");
    if (!grant) return;
    setBusy(`approve-${pol.policy_id}`);
    try {
      await axios.post(`${API}/enterprise/policies/approve`,
        { policy_id: pol.policy_id, version: pol.version, grant });
      toast.success("Policy approved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Blocked (permission or separation of duties)" : "Approval failed");
    } finally { setBusy(""); }
  };

  const activateDeployment = async (dep) => {
    const grant = await requestStepUp("provider_activation");
    if (!grant) return;
    setBusy(`act-${dep.provider_deployment_id}`);
    try {
      await axios.post(`${API}/enterprise/deployments/activate`,
        { provider_deployment_id: dep.provider_deployment_id, grant });
      toast.success("Deployment activated");
      refresh();
    } catch { toast.error("Activation failed"); } finally { setBusy(""); }
  };

  const suspendDeployment = async (dep) => {
    setBusy(`sus-${dep.provider_deployment_id}`);
    try {
      await axios.post(`${API}/enterprise/deployments/suspend`,
        { provider_deployment_id: dep.provider_deployment_id });
      toast.success("Deployment suspended");
      refresh();
    } catch { toast.error("Suspend failed"); } finally { setBusy(""); }
  };

  const rotateCredentials = async () => {
    const grant = await requestStepUp("credential_rotation");
    if (!grant) return;
    setBusy("rotate");
    try {
      const r = await axios.post(`${API}/enterprise/credentials/rotate`, { grant });
      toast.success(`Rewrapped ${r.data.rewrapped ?? 0} credential(s)`);
    } catch { toast.error("Rotation failed"); } finally { setBusy(""); }
  };

  const exportAudit = async () => {
    const grant = await requestStepUp("audit_export");
    if (!grant) return;
    setBusy("export");
    try {
      const r = await axios.get(`${API}/enterprise/audit/export`, { params: { grant } });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `enterprise-audit-${Date.now()}.json`; link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.count} record(s)`);
    } catch { toast.error("Export failed (permission or step-up)"); } finally { setBusy(""); }
  };

  const [readiness, setReadiness] = useState({});
  const checkReadiness = async (dep) => {
    const id = dep.provider_deployment_id;
    setBusy(`ready-${id}`);
    try {
      const r = await axios.get(`${API}/enterprise/deployments/${id}/readiness`);
      setReadiness((m) => ({ ...m, [id]: r.data }));
    } catch { toast.error("Readiness check failed"); } finally { setBusy(""); }
  };

  const approveSupport = async (g) => {
    setBusy(`sa-appr-${g.grant_id}`);
    try {
      await axios.post(`${API}/enterprise/support-access/approve`, { grant_id: g.grant_id });
      toast.success("Support access approved");
      refresh();
    } catch { toast.error("Approve failed (permission?)"); } finally { setBusy(""); }
  };

  const activateSupport = async (g) => {
    const grant = await requestStepUp("support_access_grant");
    if (!grant) return;
    setBusy(`sa-act-${g.grant_id}`);
    try {
      await axios.post(`${API}/enterprise/support-access/activate`, { grant_id: g.grant_id, grant });
      toast.success("Support session activated");
      refresh();
    } catch { toast.error("Activation failed (needs customer approval + step-up)"); } finally { setBusy(""); }
  };

  const revokeSupport = async (g) => {
    setBusy(`sa-rev-${g.grant_id}`);
    try {
      await axios.post(`${API}/enterprise/support-access/revoke`, { grant_id: g.grant_id });
      toast.success("Support access revoked");
      refresh();
    } catch { toast.error("Revoke failed"); } finally { setBusy(""); }
  };

  if (loading) return <div className="p-8 text-slate-400 font-mono text-sm" data-testid="ent-loading">Loading enterprise console…</div>;

  return (
    <div className="space-y-4" data-testid="enterprise-console">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-50 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-400" /> Enterprise Inference
          </h2>
          <p className="text-xs text-slate-500 font-mono mt-1">
            org: {me?.org_id} · role: {me?.role} · states reflect backend evidence only
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh} data-testid="ent-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {activeSupport.length > 0 && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 flex items-center gap-3"
          data-testid="support-access-banner">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-sm text-amber-200">
            <span className="font-semibold">Privileged support access is ACTIVE on this tenant.</span>{" "}
            <span className="font-mono text-xs text-amber-300/90">
              {activeSupport.map((g) => `${g.requester_email || g.requester_id} · ${g.ticket_reference}`).join("  |  ")}
            </span>
          </div>
          {can("ent.support.revoke") && (
            <Button size="sm" variant="destructive" className="ml-auto"
              onClick={() => revokeSupport(activeSupport[0])} data-testid="banner-revoke-support">
              <XCircle className="w-4 h-4 mr-1" /> Revoke now
            </Button>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ent-tabs" className="flex-wrap">
          <TabsTrigger value="overview" data-testid="ent-tab-overview"><Activity className="w-4 h-4 mr-1" />Release Gate</TabsTrigger>
          <TabsTrigger value="policies" data-testid="ent-tab-policies"><FileText className="w-4 h-4 mr-1" />Policies</TabsTrigger>
          <TabsTrigger value="deployments" data-testid="ent-tab-deployments"><Server className="w-4 h-4 mr-1" />Deployments</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="ent-tab-credentials"><KeyRound className="w-4 h-4 mr-1" />Credentials</TabsTrigger>
          <TabsTrigger value="dlp" data-testid="ent-tab-dlp"><ScanEye className="w-4 h-4 mr-1" />DLP</TabsTrigger>
          <TabsTrigger value="audit" data-testid="ent-tab-audit"><ScrollText className="w-4 h-4 mr-1" />Audit</TabsTrigger>
          <TabsTrigger value="identity" data-testid="ent-tab-identity"><Fingerprint className="w-4 h-4 mr-1" />Identity (SSO)</TabsTrigger>
          <TabsTrigger value="support" data-testid="ent-tab-support"><LifeBuoy className="w-4 h-4 mr-1" />Support Access</TabsTrigger>
        </TabsList>

        {/* Release Gate / status matrix */}
        <TabsContent value="overview">
          <Card className="p-5 bg-slate-900 border-slate-800">
            <h3 className="text-lg font-semibold text-slate-100 mb-1">Security Release Gate</h3>
            <p className="text-xs text-slate-500 mb-4">{status?.notes}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="status-matrix">
              {status && Object.entries(status.controls).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-3 rounded border border-slate-800 bg-slate-900/50">
                  <div>
                    <div className="text-sm text-slate-200 font-medium">{k.replace(/_/g, " ")}</div>
                    {v.evidence && <div className="text-[11px] text-slate-500 font-mono">{v.evidence}</div>}
                  </div>
                  <StateBadge state={v.state} />
                </div>
              ))}
            </div>
            <div className="mt-4 text-[11px] text-slate-500 font-mono">
              active enterprise deployments: {status?.active_deployments ?? 0}
            </div>
          </Card>
        </TabsContent>

        {/* Policies */}
        <TabsContent value="policies" className="space-y-4">
          {can("ent.policy.draft") && <PolicyAuthoring config={config} onChanged={refresh} />}
          <Card className="p-4 bg-slate-900 border-slate-800">
            <Table data-testid="policies-table">
              <TableHeader><TableRow>
                <TableHead>Policy</TableHead><TableHead>Ver</TableHead><TableHead>Class</TableHead>
                <TableHead>Status</TableHead><TableHead>Author</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {policies.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-slate-500 text-sm">No routing policies for this org.</TableCell></TableRow>
                )}
                {policies.map((p, i) => (
                  <TableRow key={i} data-testid={`policy-row-${p.policy_id}`}>
                    <TableCell className="font-mono text-slate-200">{p.policy_id}</TableCell>
                    <TableCell className="font-mono">{p.version}</TableCell>
                    <TableCell className="font-mono text-slate-400">{p.data_classification || "—"}</TableCell>
                    <TableCell><StateBadge state={p.status} /></TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{p.created_by || "—"}</TableCell>
                    <TableCell className="text-right">
                      {p.status === "pending_approval" && can("ent.policy.approve") && (
                        <Button size="sm" disabled={busy === `approve-${p.policy_id}`}
                          onClick={() => approvePolicy(p)} data-testid={`approve-${p.policy_id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Approval requires step-up MFA and passes separation-of-duties on the backend.
            </p>
          </Card>
        </TabsContent>

        {/* Deployments */}
        <TabsContent value="deployments">
          <Card className="p-4 bg-slate-900 border-slate-800">
            <Table data-testid="deployments-table">
              <TableHeader><TableRow>
                <TableHead>Deployment</TableHead><TableHead>Provider</TableHead><TableHead>Region</TableHead>
                <TableHead>Route</TableHead><TableHead>Retention</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {deployments.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-slate-500 text-sm">No provider deployments registered.</TableCell></TableRow>
                )}
                {deployments.map((d, i) => (
                  <Fragment key={i}>
                  <TableRow data-testid={`dep-row-${d.provider_deployment_id}`}>
                    <TableCell className="font-mono text-slate-200">{d.provider_deployment_id}</TableCell>
                    <TableCell className="font-mono">{d.provider}</TableCell>
                    <TableCell className="font-mono text-slate-400">{d.region || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{d.commercial_route || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-400">{d.retention_posture || "unknown"}</TableCell>
                    <TableCell><StateBadge state={d.active ? "active" : (d.approval_status || "unknown")} /></TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" disabled={busy === `ready-${d.provider_deployment_id}`}
                        onClick={() => checkReadiness(d)} data-testid={`readiness-${d.provider_deployment_id}`}>
                        <ClipboardCheck className="w-4 h-4 mr-1" /> Readiness
                      </Button>
                      {!d.active && can("ent.deployment.activate") && (
                        <Button size="sm" disabled={busy === `act-${d.provider_deployment_id}`}
                          onClick={() => activateDeployment(d)} data-testid={`activate-${d.provider_deployment_id}`}>
                          <Play className="w-4 h-4 mr-1" /> Activate
                        </Button>
                      )}
                      {d.active && can("ent.deployment.suspend") && (
                        <Button size="sm" variant="destructive" disabled={busy === `sus-${d.provider_deployment_id}`}
                          onClick={() => suspendDeployment(d)} data-testid={`suspend-${d.provider_deployment_id}`}>
                          <Ban className="w-4 h-4 mr-1" /> Suspend
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {readiness[d.provider_deployment_id] && (
                    <TableRow data-testid={`readiness-row-${d.provider_deployment_id}`}>
                      <TableCell colSpan={7} className="bg-slate-950/60">
                        <div className="flex items-center gap-2 mb-2">
                          <StateBadge state={readiness[d.provider_deployment_id].ready ? "approved" : "critical"} />
                          <span className="text-xs font-mono text-slate-400">
                            {readiness[d.provider_deployment_id].ready
                              ? "All activation evidence satisfied"
                              : `${readiness[d.provider_deployment_id].blocking_reasons.length} blocking reason(s)`}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {readiness[d.provider_deployment_id].blocking_reasons.map((rc) => (
                            <span key={rc} data-testid={`reason-${rc}`}
                              className="px-2 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 text-[11px] font-mono">
                              {rc}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Credentials */}
        <TabsContent value="credentials">
          <Card className="p-5 bg-slate-900 border-slate-800">
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Credential Health</h3>
            <p className="text-xs text-slate-500 mb-4">Credentials are AES-256-GCM encrypted and never exposed. Rotation re-wraps under the current master-key version.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {deployments.map((d, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded border border-slate-800 bg-slate-900/50">
                  <div className="font-mono text-sm text-slate-300">{d.provider_deployment_id}</div>
                  <StateBadge state={d.active ? "active" : "unknown"} />
                </div>
              ))}
            </div>
            {can("ent.credential.rotate") && (
              <Button size="sm" disabled={busy === "rotate"} onClick={rotateCredentials} data-testid="rotate-credentials">
                <RefreshCw className="w-4 h-4 mr-1" /> Rotate / Re-wrap (step-up)
              </Button>
            )}
          </Card>
        </TabsContent>

        {/* DLP */}
        <TabsContent value="dlp">
          <DlpPanel me={me} config={config} onChanged={refresh} requestStepUp={requestStepUp} />
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit">
          <Card className="p-5 bg-slate-900 border-slate-800">
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Audit Export</h3>
            <p className="text-xs text-slate-500 mb-4">Content-free provenance export (no prompts/completions/credentials). Watermarked; requires step-up.</p>
            {can("ent.audit.export") ? (
              <Button size="sm" disabled={busy === "export"} onClick={exportAudit} data-testid="export-audit">
                <ScrollText className="w-4 h-4 mr-1" /> Export audit (step-up)
              </Button>
            ) : <p className="text-sm text-slate-500">You lack ent.audit.export.</p>}
            <div className="mt-5">
              <Label2>Agreement</Label2>
              {agreement ? (
                <div className="flex items-center gap-3 text-sm font-mono text-slate-300">
                  <StateBadge state={agreement.msa_status === "active" && agreement.dpa_status === "active" ? "active" : "unknown"} />
                  MSA:{agreement.msa_status} · DPA:{agreement.dpa_status}
                </div>
              ) : <StateBadge state="unknown" />}
            </div>
          </Card>
        </TabsContent>

        {/* Identity / SSO */}
        <TabsContent value="identity">
          <IdentityPanel me={me} connectors={connectors} fedAvail={fedAvail} onChanged={refresh} />
        </TabsContent>

        {/* Privileged support access */}
        <TabsContent value="support">
          <SupportAccessPanel
            me={me} grants={supportGrants} busy={busy} config={config}
            onApprove={approveSupport} onActivate={activateSupport} onRevoke={revokeSupport}
            onChanged={refresh} />
        </TabsContent>
      </Tabs>

      {/* Step-up dialog */}
      <Dialog open={stepUp.open} onOpenChange={(o) => !o && setStepUp({ open: false, action: null, code: "", resolve: stepUp.resolve && (() => stepUp.resolve(null)) })}>
        <DialogContent className="bg-slate-900 border-slate-800" data-testid="step-up-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> Step-up verification</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Action: <span className="font-mono text-slate-200">{stepUp.action}</span>. Enter your MFA / backup code to authorize.</p>
          <Input autoFocus type="text" inputMode="numeric" placeholder="123456"
            value={stepUp.code} data-testid="step-up-code"
            onChange={(e) => setStepUp((s) => ({ ...s, code: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && submitStepUp()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { stepUp.resolve && stepUp.resolve(null); setStepUp({ open: false, action: null, code: "", resolve: null }); }}>Cancel</Button>
            <Button onClick={submitStepUp} data-testid="step-up-submit">Verify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── DLP panel with policy authoring + synthetic simulator + emergency deny ──
const DlpPanel = ({ me, config, onChanged, requestStepUp }) => {
  const [detectors, setDetectors] = useState("pii_email");
  const [action, setAction] = useState("redact");
  const [samples, setSamples] = useState("Please email john.doe@acme.com and card 4111 1111 1111 1111");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const can = (p) => me?.permissions?.includes(p);

  // DLP policy authoring (draft → submit → approve)
  const [dlpPolicies, setDlpPolicies] = useState([]);
  const [draft, setDraft] = useState({ dlp_policy_id: "", enabled_detectors: "pii_email", action: "deny", binary_content_action: "deny", approved_data_classes: "confidential" });
  const [authBusy, setAuthBusy] = useState("");

  const loadPolicies = useCallback(async () => {
    if (!can("ent.dlp.manage")) return;
    try {
      const r = await axios.get(`${API}/enterprise/dlp/policies`);
      setDlpPolicies(r.data.policies || []);
    } catch { /* permission */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 'can' derives from me; recompute only when me changes
  }, [me]);
  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const createDraft = async () => {
    if (!draft.dlp_policy_id.trim()) { toast.error("Policy id required"); return; }
    if (!config?.config_version) { toast.error("Configuration unavailable"); return; }
    setAuthBusy("draft");
    try {
      await axios.post(`${API}/enterprise/dlp/policies/draft`, {
        dlp_policy_id: draft.dlp_policy_id.trim(),
        enabled_detectors: draft.enabled_detectors.split(",").map((s) => s.trim()).filter(Boolean),
        action: draft.action, binary_content_action: draft.binary_content_action,
        approved_data_classes: draft.approved_data_classes.split(",").map((s) => s.trim()).filter(Boolean),
        config_version: config.config_version,
      });
      toast.success("DLP policy draft created");
      loadPolicies();
    } catch (e) {
      const err = e?.response?.data?.error;
      toast.error(err === "stale_configuration_version" ? "Configuration changed — reload and retry" : "Draft failed (permission / config?)");
    } finally { setAuthBusy(""); }
  };

  const submitPolicy = async (p) => {
    setAuthBusy(`sub-${p.dlp_policy_id}-${p.version}`);
    try {
      await axios.post(`${API}/enterprise/dlp/policies/submit`, { dlp_policy_id: p.dlp_policy_id, version: p.version });
      toast.success("Submitted for approval");
      loadPolicies();
    } catch { toast.error("Submit failed"); } finally { setAuthBusy(""); }
  };

  const approvePolicy = async (p) => {
    const grant = await requestStepUp("dlp_weakening");
    if (!grant) return;
    setAuthBusy(`app-${p.dlp_policy_id}-${p.version}`);
    try {
      await axios.post(`${API}/enterprise/dlp/policies/approve`, { dlp_policy_id: p.dlp_policy_id, version: p.version, grant });
      toast.success("DLP policy approved (now enforced)");
      loadPolicies(); onChanged();
    } catch { toast.error("Approval blocked (permission / separation of duties / step-up)"); } finally { setAuthBusy(""); }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/enterprise/dlp/simulate`, {
        policy: { enabled_detectors: detectors.split(",").map((s) => s.trim()).filter(Boolean), action },
        samples: samples.split("\n").filter(Boolean),
      });
      setResult(r.data);
    } catch { toast.error("Simulation failed (permission?)"); } finally { setBusy(false); }
  };

  const emergency = async (enabled) => {
    try {
      await axios.post(`${API}/enterprise/dlp/emergency-deny`, { enabled });
      toast.success(`Emergency deny ${enabled ? "ENABLED" : "disabled"}`);
      onChanged();
    } catch { toast.error("Failed (permission?)"); }
  };

  return (
    <div className="space-y-4" data-testid="dlp-tab">
      {can("ent.dlp.manage") && !config?.config_version && (
        <Card className="p-5 bg-amber-500/5 border-amber-500/30" data-testid="dlp-config-unavailable">
          <p className="text-sm text-amber-300">Configuration unavailable — DLP policy authoring is disabled until the canonical authoring configuration is available.</p>
        </Card>
      )}
      {can("ent.dlp.manage") && config?.config_version && (
        <Card className="p-5 bg-slate-900 border-slate-800 space-y-3" data-testid="dlp-authoring">
          <h3 className="text-lg font-semibold text-slate-100">DLP Policy Authoring</h3>
          <p className="text-xs text-slate-500">Author a versioned DLP policy from live detectors. Draft → submit → approve (approval requires separation of duties + step-up and takes effect on egress).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label2>Policy id</Label2>
              <Input value={draft.dlp_policy_id} onChange={(e) => setDraft((d) => ({ ...d, dlp_policy_id: e.target.value }))}
                data-testid="dlp-draft-id" className="font-mono bg-slate-950 border-slate-800" placeholder="dlp-standard" /></div>
            <div><Label2>Detectors (comma-sep)</Label2>
              <Input value={draft.enabled_detectors} onChange={(e) => setDraft((d) => ({ ...d, enabled_detectors: e.target.value }))}
                data-testid="dlp-draft-detectors" className="font-mono bg-slate-950 border-slate-800" /></div>
            <div><Label2>Action</Label2>
              <select value={draft.action} onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
                data-testid="dlp-draft-action" className="w-full h-9 rounded-md bg-slate-950 border border-slate-800 text-sm px-2 text-slate-200">
                {(config?.dlp_actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select></div>
            <div><Label2>Binary / multimodal action</Label2>
              <select value={draft.binary_content_action} onChange={(e) => setDraft((d) => ({ ...d, binary_content_action: e.target.value }))}
                data-testid="dlp-draft-binary" className="w-full h-9 rounded-md bg-slate-950 border border-slate-800 text-sm px-2 text-slate-200">
                {(config?.binary_content_actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select></div>
            <div className="md:col-span-2"><Label2>Approved data classes (comma-sep)</Label2>
              <Input value={draft.approved_data_classes} onChange={(e) => setDraft((d) => ({ ...d, approved_data_classes: e.target.value }))}
                data-testid="dlp-draft-classes" className="font-mono bg-slate-950 border-slate-800" /></div>
          </div>
          <Button size="sm" disabled={authBusy === "draft"} onClick={createDraft} data-testid="dlp-draft-create">
            <Plus className="w-4 h-4 mr-1" /> Create draft
          </Button>
          {dlpPolicies.length > 0 && (
            <Table data-testid="dlp-policies-table">
              <TableHeader><TableRow>
                <TableHead>Policy</TableHead><TableHead>Ver</TableHead><TableHead>Action</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {dlpPolicies.map((p, i) => (
                  <TableRow key={i} data-testid={`dlp-policy-row-${p.dlp_policy_id}-${p.version}`}>
                    <TableCell className="font-mono text-slate-200">{p.dlp_policy_id}</TableCell>
                    <TableCell className="font-mono">{p.version}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-400">{p.action}</TableCell>
                    <TableCell><StateBadge state={p.status} /></TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      {p.status === "draft" && (
                        <Button size="sm" variant="outline" disabled={authBusy === `sub-${p.dlp_policy_id}-${p.version}`}
                          onClick={() => submitPolicy(p)} data-testid={`dlp-submit-${p.dlp_policy_id}-${p.version}`}>Submit</Button>
                      )}
                      {p.status === "pending_approval" && (
                        <Button size="sm" disabled={authBusy === `app-${p.dlp_policy_id}-${p.version}`}
                          onClick={() => approvePolicy(p)} data-testid={`dlp-approve-${p.dlp_policy_id}-${p.version}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    <Card className="p-5 bg-slate-900 border-slate-800 space-y-4" data-testid="dlp-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-100">DLP Simulator (synthetic data)</h3>
        {can("ent.emergency.deny") && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-rose-400 font-mono uppercase tracking-wider">Emergency deny</span>
            <Switch onCheckedChange={emergency} data-testid="emergency-deny-switch" />
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">Preview deny / redact / tokenize outcomes. Nothing is stored.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label2>Detectors (comma-sep)</Label2>
          <Input value={detectors} onChange={(e) => setDetectors(e.target.value)} data-testid="dlp-detectors"
            className="font-mono bg-slate-950 border-slate-800" /></div>
        <div><Label2>Action</Label2>
          <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="dlp-action"
            className="w-full h-9 rounded-md bg-slate-950 border border-slate-800 text-sm px-2 text-slate-200">
            {(config?.dlp_actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select></div>
      </div>
      <div><Label2>Synthetic samples (one per line)</Label2>
        <textarea value={samples} onChange={(e) => setSamples(e.target.value)} data-testid="dlp-samples" rows={3}
          className="w-full rounded-md bg-slate-950 border border-slate-800 text-sm p-2 font-mono text-slate-200" /></div>
      {can("ent.dlp.manage") ? (
        <Button size="sm" disabled={busy} onClick={simulate} data-testid="dlp-simulate-btn">
          <Play className="w-4 h-4 mr-1" /> Simulate
        </Button>
      ) : <p className="text-sm text-slate-500">You lack ent.dlp.manage.</p>}
      {result && (
        <div className="space-y-2" data-testid="dlp-sim-results">
          {result.results.map((r, i) => (
            <div key={i} className="p-3 rounded border border-slate-800 bg-slate-900/50">
              <div className="flex items-center gap-2 mb-1">
                <StateBadge state={r.action === "deny" ? "revoked" : (r.action === "allow" ? "active" : "pending_approval")} />
                <span className="text-xs font-mono text-slate-400">{r.findings.length} finding(s)</span>
              </div>
              {r.preview != null && <div className="text-sm font-mono text-slate-200 break-words">{r.preview}</div>}
            </div>
          ))}
        </div>
      )}
    </Card>
    </div>
  );
};

// ── Identity / SSO connector framework panel ──
const IdentityPanel = ({ me, connectors, fedAvail, onChanged }) => {
  const [test, setTest] = useState(null);
  const can = (p) => me?.permissions?.includes(p);

  const runTest = async (cid) => {
    try {
      const r = await axios.post(`${API}/enterprise/identity/connectors/test`, {
        connector_id: cid, sample_claims: { email: "user@example.com", groups: ["ent-admins"] },
      });
      setTest(r.data);
      toast.success("Test harness executed (no network, no enable)");
    } catch { toast.error("Test failed"); }
  };

  return (
    <Card className="p-5 bg-slate-900 border-slate-800 space-y-4" data-testid="identity-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-100">Federated Identity (OIDC / SAML)</h3>
        <StateBadge state={fedAvail ? "active" : "disabled"} />
      </div>
      <div className="p-3 rounded border border-amber-400/20 bg-amber-400/5 text-amber-300 text-xs">
        Framework only. Connectors are disabled by default; no live connector is active. Client secrets are entered
        via the secure server-side workflow and never shown here. Federated identity is NOT marked available until a
        real connector is tested successfully.
      </div>
      <Table data-testid="connectors-table">
        <TableHeader><TableRow>
          <TableHead>Connector</TableHead><TableHead>Protocol</TableHead><TableHead>Enabled</TableHead>
          <TableHead>Status</TableHead><TableHead>Secret</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {connectors.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-slate-500 text-sm">No connectors configured.</TableCell></TableRow>
          )}
          {connectors.map((c, i) => (
            <TableRow key={i} data-testid={`connector-${c.connector_id}`}>
              <TableCell className="font-mono text-slate-200">{c.connector_id}</TableCell>
              <TableCell className="font-mono uppercase">{c.protocol}</TableCell>
              <TableCell><StateBadge state={c.enabled ? "active" : "disabled"} /></TableCell>
              <TableCell><StateBadge state={c.status} /></TableCell>
              <TableCell className="font-mono text-xs">{c.client_secret_configured || c.idp_cert_configured ? "configured" : "—"}</TableCell>
              <TableCell className="text-right">
                {can("ent.identity.manage") && (
                  <Button size="sm" variant="outline" onClick={() => runTest(c.connector_id)}
                    data-testid={`test-connector-${c.connector_id}`}>Test</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {test && (
        <div className="p-3 rounded border border-slate-800 bg-slate-950 text-xs font-mono text-slate-300" data-testid="connector-test-result">
          config_valid: {String(test.config_valid)} · problems: {test.problems.join(", ") || "none"} ·
          mapped role: {test.mapping?.role || test.mapping_error}
        </div>
      )}
    </Card>
  );
};

// ── Routing policy authoring (draft → submit), driven by live config ──
const PolicyAuthoring = ({ config, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [f, setF] = useState({
    policy_id: "", data_classification: "internal",
    allowed_deployment_classes: [], allowed_regions: "", allowed_model_ids: "",
    denied_model_ids: "", passthrough_allowed: false, managed_fallback_allowed: false,
  });
  const toggleClass = (c) =>
    setF((s) => ({ ...s, allowed_deployment_classes: s.allowed_deployment_classes.includes(c)
      ? s.allowed_deployment_classes.filter((x) => x !== c) : [...s.allowed_deployment_classes, c] }));

  const csv = (v) => v.split(",").map((s) => s.trim()).filter(Boolean);

  const create = async (submit) => {
    if (!f.policy_id.trim()) { toast.error("Policy id required"); return; }
    if (!config?.config_version) { toast.error("Configuration unavailable"); return; }
    setBusy("create");
    try {
      const draft = await axios.post(`${API}/enterprise/policies/draft`, {
        policy_id: f.policy_id.trim(), data_classification: f.data_classification,
        allowed_deployment_classes: f.allowed_deployment_classes,
        allowed_regions: csv(f.allowed_regions), allowed_model_ids: csv(f.allowed_model_ids),
        denied_model_ids: csv(f.denied_model_ids),
        passthrough_allowed: f.passthrough_allowed, managed_fallback_allowed: f.managed_fallback_allowed,
        config_version: config.config_version,
      });
      if (submit) {
        await axios.post(`${API}/enterprise/policies/submit`,
          { policy_id: draft.data.policy_id, version: draft.data.version });
        toast.success("Policy drafted & submitted for approval");
      } else { toast.success("Policy draft saved"); }
      setOpen(false); onChanged();
    } catch (e) {
      const err = e?.response?.data?.error;
      toast.error(err === "stale_configuration_version" ? "Configuration changed — reload and retry"
        : err === "unknown_config_value" ? "Value not in current configuration" : "Draft failed (permission / config?)");
    } finally { setBusy(""); }
  };

  return (
    <Card className="p-4 bg-slate-900 border-slate-800" data-testid="policy-authoring">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Author routing policy</h3>
          <p className="text-xs text-slate-500">Build from live providers, models, regions & deployment classes.</p>
        </div>
        <Button size="sm" variant={open ? "outline" : "default"} disabled={!config?.config_version} onClick={() => setOpen((o) => !o)} data-testid="policy-authoring-toggle">
          <Plus className="w-4 h-4 mr-1" /> {open ? "Close" : "New policy"}
        </Button>
      </div>
      {!config?.config_version && (
        <p className="mt-2 text-xs text-amber-300" data-testid="policy-config-unavailable">Configuration unavailable — policy authoring is disabled.</p>
      )}
      {open && (
        <div className="mt-4 space-y-3" data-testid="policy-authoring-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label2>Policy id</Label2>
              <Input value={f.policy_id} onChange={(e) => setF((s) => ({ ...s, policy_id: e.target.value }))}
                data-testid="policy-id" className="font-mono bg-slate-950 border-slate-800" placeholder="managed-confidential" /></div>
            <div><Label2>Data classification</Label2>
              <select value={f.data_classification} onChange={(e) => setF((s) => ({ ...s, data_classification: e.target.value }))}
                data-testid="policy-data-class" className="w-full h-9 rounded-md bg-slate-950 border border-slate-800 text-sm px-2 text-slate-200">
                {(config?.data_classifications || ["internal"]).map((x) => <option key={x} value={x}>{x}</option>)}
              </select></div>
          </div>
          <div>
            <Label2>Allowed deployment classes</Label2>
            <div className="flex flex-wrap gap-2" data-testid="policy-deployment-classes">
              {(config?.deployment_classes || []).map((c) => (
                <button key={c} type="button" onClick={() => toggleClass(c)}
                  data-testid={`policy-class-${c}`}
                  className={`px-2 py-1 rounded border text-xs font-mono ${f.allowed_deployment_classes.includes(c)
                    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                    : "border-slate-700 bg-slate-950 text-slate-400"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label2>Allowed regions (comma-sep)</Label2>
              <Input value={f.allowed_regions} onChange={(e) => setF((s) => ({ ...s, allowed_regions: e.target.value }))}
                data-testid="policy-regions" className="font-mono bg-slate-950 border-slate-800"
                placeholder={(config?.regions || []).slice(0, 2).join(", ")} /></div>
            <div><Label2>Allowed models (comma-sep)</Label2>
              <Input value={f.allowed_model_ids} onChange={(e) => setF((s) => ({ ...s, allowed_model_ids: e.target.value }))}
                data-testid="policy-models" className="font-mono bg-slate-950 border-slate-800"
                placeholder={(config?.models || []).slice(0, 2).join(", ")} /></div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-xs text-slate-300"><Switch checked={f.passthrough_allowed}
              onCheckedChange={(v) => setF((s) => ({ ...s, passthrough_allowed: v }))} data-testid="policy-passthrough" /> Passthrough allowed</label>
            <label className="flex items-center gap-2 text-xs text-slate-300"><Switch checked={f.managed_fallback_allowed}
              onCheckedChange={(v) => setF((s) => ({ ...s, managed_fallback_allowed: v }))} data-testid="policy-fallback" /> Managed fallback</label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy === "create"} onClick={() => create(false)} data-testid="policy-save-draft">Save draft</Button>
            <Button size="sm" disabled={busy === "create"} onClick={() => create(true)} data-testid="policy-submit">Draft & submit for approval</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// ── Privileged support access (NOT a tenant switcher) ──
const SUPPORT_SCOPE_OPTS = ["ent.deployment.view", "ent.policy.view", "ent.agreement.view"];

const SupportAccessPanel = ({ me, grants, busy, config, onApprove, onActivate, onRevoke, onChanged }) => {
  const isSupport = me?.is_support_operator === true;
  const can = (p) => me?.permissions?.includes(p);
  const reasonCodes = config?.support_reason_codes || [];
  const [req, setReq] = useState({ org_id: "", reason_code: "", ticket_reference: "", note: "", scope: ["ent.deployment.view"], ttl_minutes: 60 });
  const [reqBusy, setReqBusy] = useState(false);
  const [view, setView] = useState(null);

  const toggleScope = (s) => setReq((r) => ({ ...r, scope: r.scope.includes(s) ? r.scope.filter((x) => x !== s) : [...r.scope, s] }));

  const submitRequest = async () => {
    if (!req.org_id.trim() || !req.reason_code || !req.ticket_reference.trim()) {
      toast.error("Target org, reason code and ticket are required"); return;
    }
    setReqBusy(true);
    try {
      const payload = { org_id: req.org_id, reason_code: req.reason_code,
        ticket_reference: req.ticket_reference, scope: req.scope, ttl_minutes: req.ttl_minutes };
      if (req.note.trim()) payload.note = req.note.trim();
      await axios.post(`${API}/enterprise/support-access/request`, payload);
      toast.success("Support access requested (awaiting customer approval)");
      onChanged();
    } catch (e) {
      const err = e?.response?.data?.error;
      const msg = { invalid_reason_code: "Pick a valid reason code",
        invalid_ticket_reference_format: "Ticket reference format is invalid",
        note_contains_sensitive_data: "Note contains sensitive data — remove secrets/PII" }[err];
      toast.error(msg || "Request failed (support role required)");
    } finally { setReqBusy(false); }
  };

  const openView = async (g) => {
    try {
      const r = await axios.get(`${API}/enterprise/support-access/${g.grant_id}/view`);
      setView(r.data);
    } catch { toast.error("View denied (no active grant)"); }
  };

  return (
    <div className="space-y-4" data-testid="support-access-panel">
      <Card className="p-5 bg-slate-900 border-slate-800 space-y-2">
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-indigo-400" /> Privileged support access</h3>
        <p className="text-xs text-slate-500">Not a tenant switcher. Each grant is separately requested (reason + ticket), customer-approved when required, step-up activated, time-limited, visibly indicated and instantly revocable. Support never receives provider credentials or DLP token maps.</p>
      </Card>

      {isSupport && (
        <Card className="p-5 bg-slate-900 border-slate-800 space-y-3" data-testid="support-request-form">
          <h4 className="text-sm font-semibold text-slate-200 flex items-center gap-2"><Users className="w-4 h-4" /> Request access to a tenant</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label2>Target org id</Label2>
              <Input value={req.org_id} onChange={(e) => setReq((r) => ({ ...r, org_id: e.target.value }))}
                data-testid="sa-org" className="font-mono bg-slate-950 border-slate-800" /></div>
            <div><Label2>Ticket reference</Label2>
              <Input value={req.ticket_reference} onChange={(e) => setReq((r) => ({ ...r, ticket_reference: e.target.value }))}
                data-testid="sa-ticket" className="font-mono bg-slate-950 border-slate-800" placeholder="SUP-1234" /></div>
            <div><Label2>TTL (minutes, max 240)</Label2>
              <Input type="number" min={5} max={240} value={req.ttl_minutes}
                onChange={(e) => setReq((r) => ({ ...r, ttl_minutes: Number(e.target.value) }))}
                data-testid="sa-ttl" className="font-mono bg-slate-950 border-slate-800" /></div>
          </div>
          <div><Label2>Reason code</Label2>
            <select value={req.reason_code} onChange={(e) => setReq((r) => ({ ...r, reason_code: e.target.value }))}
              data-testid="sa-reason-code" className="w-full h-9 rounded-md bg-slate-950 border border-slate-800 text-sm px-2 text-slate-200">
              <option value="">Select a reason code…</option>
              {reasonCodes.map((rc) => <option key={rc} value={rc}>{rc}</option>)}
            </select></div>
          <div><Label2>Optional note (screened; secrets/PII rejected; encrypted & auto-deleted)</Label2>
            <Input value={req.note} onChange={(e) => setReq((r) => ({ ...r, note: e.target.value }))}
              data-testid="sa-note" className="bg-slate-950 border-slate-800" placeholder="Optional context — no secrets or personal data" /></div>
          <div>
            <Label2>Least-privilege scope (read-only)</Label2>
            <div className="flex flex-wrap gap-2" data-testid="sa-scope">
              {SUPPORT_SCOPE_OPTS.map((s) => (
                <button key={s} type="button" onClick={() => toggleScope(s)} data-testid={`sa-scope-${s}`}
                  className={`px-2 py-1 rounded border text-xs font-mono ${req.scope.includes(s)
                    ? "border-indigo-400/40 bg-indigo-400/10 text-indigo-300" : "border-slate-700 bg-slate-950 text-slate-400"}`}>{s}</button>
              ))}
            </div>
          </div>
          <Button size="sm" disabled={reqBusy} onClick={submitRequest} data-testid="sa-request-btn">
            <Plus className="w-4 h-4 mr-1" /> Request access
          </Button>
        </Card>
      )}

      <Card className="p-4 bg-slate-900 border-slate-800">
        <Table data-testid="support-grants-table">
          <TableHeader><TableRow>
            <TableHead>Grant</TableHead><TableHead>Tenant</TableHead><TableHead>Ticket</TableHead>
            <TableHead>Scope</TableHead><TableHead>Status</TableHead><TableHead>Expires</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(!grants || grants.length === 0) && (
              <TableRow><TableCell colSpan={7} className="text-slate-500 text-sm">No support access grants.</TableCell></TableRow>
            )}
            {(grants || []).map((g, i) => (
              <TableRow key={i} data-testid={`sa-row-${g.grant_id}`}>
                <TableCell className="font-mono text-xs text-slate-300">{g.grant_id}</TableCell>
                <TableCell className="font-mono text-xs text-slate-400">{g.org_id}</TableCell>
                <TableCell className="font-mono text-xs">{g.ticket_reference}</TableCell>
                <TableCell className="font-mono text-[10px] text-slate-400">{(g.scope || []).join(", ")}</TableCell>
                <TableCell><StateBadge state={g.status === "pending_customer_approval" ? "pending_approval" : g.status} /></TableCell>
                <TableCell className="font-mono text-[10px] text-slate-500">{g.expires_at ? new Date(g.expires_at).toLocaleTimeString() : "—"}</TableCell>
                <TableCell className="text-right space-x-2 whitespace-nowrap">
                  {g.status === "pending_customer_approval" && can("ent.support.approve") && (
                    <Button size="sm" disabled={busy === `sa-appr-${g.grant_id}`} onClick={() => onApprove(g)} data-testid={`sa-approve-${g.grant_id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  )}
                  {g.status === "approved" && isSupport && (
                    <Button size="sm" disabled={busy === `sa-act-${g.grant_id}`} onClick={() => onActivate(g)} data-testid={`sa-activate-${g.grant_id}`}>
                      <Play className="w-4 h-4 mr-1" /> Activate (step-up)
                    </Button>
                  )}
                  {g.status === "active" && isSupport && (
                    <Button size="sm" variant="outline" onClick={() => openView(g)} data-testid={`sa-view-${g.grant_id}`}>
                      <ClipboardCheck className="w-4 h-4 mr-1" /> View
                    </Button>
                  )}
                  {["active", "approved", "pending_customer_approval"].includes(g.status) && (
                    <Button size="sm" variant="destructive" disabled={busy === `sa-rev-${g.grant_id}`} onClick={() => onRevoke(g)} data-testid={`sa-revoke-${g.grant_id}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {view && (
          <div className="mt-4 p-3 rounded border border-indigo-400/20 bg-indigo-400/5" data-testid="sa-view-result">
            <div className="text-xs font-mono text-indigo-300 mb-2">Scoped read of {view.org_id} · scope: {view.scope.join(", ")} · expires {view.expires_at ? new Date(view.expires_at).toLocaleTimeString() : "—"}</div>
            <pre className="text-[10px] text-slate-300 overflow-auto max-h-48">{JSON.stringify(view.deployments || view.policies || {}, null, 1)}</pre>
          </div>
        )}
      </Card>
    </div>
  );
};

